/**
 * Versioned localStorage wrapper for the Uploads Simulator composer.
 *
 * Modeled on `open-sso/openSsoStorage.js`: a tiny, dependency-free module that
 * owns its keys and never lets a corrupt payload break the page. Every read is
 * wrapped in try/catch JSON tolerance — a malformed value simply behaves like
 * an empty store. Keys are versioned (`.v1`) so a future schema change can
 * migrate (or ignore) old payloads without clashing.
 *
 * Two stores:
 *  - presets: named SOR snapshots the admin saves explicitly.
 *  - last-sor: write-through of the composer state so a reload (or an
 *    accidental navigation) restores the authored SOR.
 */

export const STORAGE_KEYS = Object.freeze({
    PRESETS: "clockout-admin.uploads-simulator.presets.v1",
    LAST_SOR: "clockout-admin.uploads-simulator.last-sor.v1"
});

/** Hard cap so a runaway save loop can never fill localStorage. */
const MAX_PRESETS = 50;

function getStore() {
    try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        return window.localStorage;
    } catch (error) {
        // Accessing localStorage itself can throw (privacy mode / blocked cookies).
        return null;
    }
}

function safeRead(key) {
    const store = getStore();
    if (!store) return null;
    try {
        const raw = store.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
}

function safeWrite(key, value) {
    const store = getStore();
    if (!store) return false;
    try {
        store.setItem(key, JSON.stringify(value));
        return true;
    } catch (error) {
        // Quota exceeded / serialization failure — treat as a soft failure.
        return false;
    }
}

function isValidPreset(entry) {
    return !!entry && typeof entry === "object" && typeof entry.name === "string" && entry.name.length > 0 && !!entry.sor && typeof entry.sor === "object";
}

/**
 * All saved presets, tolerant of corrupt or legacy payloads.
 * @returns {{name: string, sor: object, savedAt: string}[]}
 */
export function listPresets() {
    const parsed = safeRead(STORAGE_KEYS.PRESETS);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidPreset);
}

/**
 * Upsert a preset by name and return the updated list. The name is the
 * identity — saving an existing name overwrites its SOR.
 *
 * @param {string} name
 * @param {object} sor authored (dollar-denominated) SOR snapshot
 * @returns {{name: string, sor: object, savedAt: string}[]}
 */
export function savePreset(name, sor) {
    const trimmed = typeof name === "string" ? name.trim() : "";
    if (!trimmed || !sor || typeof sor !== "object") return listPresets();

    const entry = {name: trimmed, sor, savedAt: new Date().toISOString()};
    let next = [...listPresets().filter(preset => preset.name !== trimmed), entry];
    // At the cap, evict the OLDEST presets (by savedAt) — never the one just
    // saved, and never by alphabetical accident.
    if (next.length > MAX_PRESETS) {
        next = next.sort((a, b) => String(b.savedAt || "").localeCompare(String(a.savedAt || ""))).slice(0, MAX_PRESETS);
    }
    next = next.sort((a, b) => a.name.localeCompare(b.name));

    safeWrite(STORAGE_KEYS.PRESETS, next);
    return next;
}

/**
 * Delete a preset by name and return the updated list.
 * @param {string} name
 * @returns {{name: string, sor: object, savedAt: string}[]}
 */
export function deletePreset(name) {
    const next = listPresets().filter(preset => preset.name !== name);
    safeWrite(STORAGE_KEYS.PRESETS, next);
    return next;
}

/**
 * The last SOR the composer held, or null when never saved / corrupt.
 * @returns {object | null}
 */
export function loadLastSor() {
    const parsed = safeRead(STORAGE_KEYS.LAST_SOR);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed.sor && typeof parsed.sor === "object" ? parsed.sor : null;
}

/**
 * Write-through of the composer SOR. Safe to call on every edit.
 * @param {object} sor
 */
export function saveLastSor(sor) {
    if (!sor || typeof sor !== "object") return;
    safeWrite(STORAGE_KEYS.LAST_SOR, {v: 1, savedAt: new Date().toISOString(), sor});
}
